import React, { useState, useEffect } from 'react';
import "../css/Students.css";
import { User, Mail, Phone, Hash, BookOpen, Building, Users, Calendar, Save, Edit, Trash2 } from 'lucide-react';

function Students() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        rollNo: '',
        class: '',
        department: '',
        gender: '',
        batch: ''
    });

    const [students, setStudents] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [editMode, setEditMode] = useState(false);

    // Fetch all students on component mount
    useEffect(() => {
        fetchStudents();
    }, []);

    // Fetch all students from the backend
    const fetchStudents = async () => {
        try {
            const response = await fetch('https://payroll-api.uniums.com/api/students/students');
            if (!response.ok) {
                throw new Error('Failed to fetch students');
            }
            const data = await response.json();
            setStudents(data);
        } catch (error) {
            setMessage({ text: error.message, type: 'error' });
            setTimeout(() => {
                setMessage({ text: '', type: '' });
            }, 3000);
        }
    };

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const url = editMode 
                ? `https://payroll-api.uniums.com/api/students/update-student/${formData.rollNo}`
                : 'https://payroll-api.uniums.com/api/students/add-student';
            
            const method = editMode ? 'PUT' : 'POST';
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to process request');
            }

            setMessage({ 
                text: editMode ? 'Student updated successfully!' : 'Student added successfully!', 
                type: 'success' 
            });
            
            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                rollNo: '',
                class: '',
                department: '',
                gender: '',
                batch: ''
            });
            
            setEditMode(false);
            fetchStudents(); // Refresh the list
        } catch (error) {
            setMessage({ text: error.message, type: 'error' });
        } finally {
            setIsSubmitting(false);
            setTimeout(() => {
                setMessage({ text: '', type: '' });
            }, 3000);
        }
    };

    // Handle edit student
    const handleEdit = (student) => {
        setFormData({
            name: student.name,
            email: student.email,
            phone: student.phone,
            rollNo: student.rollNo,
            class: student.class,
            department: student.department,
            gender: student.gender,
            batch: student.batch
        });
        setEditMode(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle delete student
    const handleDelete = async (rollNo) => {
        if (!window.confirm('Are you sure you want to delete this student?')) {
            return;
        }

        try {
            const response = await fetch(`https://payroll-api.uniums.com/api/students/delete-student/${rollNo}`, {
                method: 'DELETE',
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete student');
            }

            setMessage({ text: 'Student deleted successfully!', type: 'success' });
            fetchStudents(); // Refresh the list
        } catch (error) {
            setMessage({ text: error.message, type: 'error' });
        } finally {
            setTimeout(() => {
                setMessage({ text: '', type: '' });
            }, 3000);
        }
    };

    return (
        <div className="students-container">
            {message.text && (
                <div className={`message ${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="registration-card">
                <div className="card-header">
                    <h2>{editMode ? 'Update Student' : 'Add New Student'}</h2>
                    <p>{editMode ? 'Update the student details' : 'Fill in the details to register a new student'}</p>
                </div>

                <form onSubmit={handleSubmit} className="registration-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="name">
                                <User size={18} />
                                <span>Student Name</span>
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter full name"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">
                                <Mail size={18} />
                                <span>Email Address</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter email address"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">
                                <Phone size={18} />
                                <span>Phone Number</span>
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="Enter phone number"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="rollNo">
                                <Hash size={18} />
                                <span>Roll Number</span>
                            </label>
                            <input
                                type="text"
                                id="rollNo"
                                name="rollNo"
                                value={formData.rollNo}
                                onChange={handleChange}
                                placeholder="Enter roll number"
                                required
                                disabled={editMode} // Disable editing roll number in edit mode
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="class">
                                <BookOpen size={18} />
                                <span>Class</span>
                            </label>
                            <input
                                type="text"
                                id="class"
                                name="class"
                                value={formData.class}
                                onChange={handleChange}
                                placeholder="Enter class"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="department">
                                <Building size={18} />
                                <span>Department</span>
                            </label>
                            <input
                                type="text"
                                id="department"
                                name="department"
                                value={formData.department}
                                onChange={handleChange}
                                placeholder="Enter department"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="gender">
                                <Users size={18} />
                                <span>Gender</span>
                            </label>
                            <select
                                id="gender"
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Gender</option>
                                <option value="male">Male</option>
                                <option value="female">Female</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="batch">
                                <Calendar size={18} />
                                <span>Admission Year/Batch</span>
                            </label>
                            <input
                                type="text"
                                id="batch"
                                name="batch"
                                value={formData.batch}
                                onChange={handleChange}
                                placeholder="Enter batch year"
                                required
                            />
                        </div>

                        <div className="form-group button-group">
                            <button
                                type="submit"
                                className="submit-button"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <span className="spinner"></span>
                                ) : (
                                    <>
                                        <Save size={18} />
                                        <span>{editMode ? 'Update' : 'Add'} Student</span>
                                    </>
                                )}
                            </button>
                            {editMode && (
                                <button
                                    type="button"
                                    className="cancel-button"
                                    onClick={() => {
                                        setFormData({
                                            name: '',
                                            email: '',
                                            phone: '',
                                            rollNo: '',
                                            class: '',
                                            department: '',
                                            gender: '',
                                            batch: ''
                                        });
                                        setEditMode(false);
                                    }}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>

            {students.length > 0 && (
                <div className="students-list-container">
                    <h2>Student Records</h2>
                    <div className="students-list">
                        {students.map(student => (
                            <div key={student._id || student.rollNo} className="student-card">
                                <div className="student-initials">
                                    {student.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="student-info1">
                                    <h3>{student.name}</h3>
                                    <p className="student-details">
                                        <span>{student.department}</span> •
                                        <span>Roll: {student.rollNo}</span> •
                                        <span>Batch: {student.batch}</span>
                                    </p>
                                    <p className="student-contact">
                                        <Mail size={14} /> {student.email} |
                                        <Phone size={14} /> {student.phone}
                                    </p>
                                </div>
                                <div className="student-actions">
                                    <button 
                                        className="edit-button" 
                                        onClick={() => handleEdit(student)}
                                    >
                                        <Edit size={16} />
                                    </button>
                                    <button 
                                        className="delete-button1" 
                                        onClick={() => handleDelete(student.rollNo)}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default Students;